import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Card, CardContent, Typography, Chip, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, Grid, Alert, LinearProgress, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, FormControlLabel } from "@mui/material";
import AdminLayout from "../AdminLayout";
import { useAuth } from "../../hooks/useAuth";
import ev from "../../services/eventV2Service";
import AdminEventQuizDialog from "./AdminEventQuizDialog";

export default function AdminEventV2Detail() {
  const { eventId } = useParams();
  const { userProfile } = useAuth();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [stageDialog, setStageDialog] = useState({ open: false, stage: null, newStatus: "active", dueDate: "", progress: 0 });
  const [stageForms, setStageForms] = useState({});
  const [registrations, setRegistrations] = useState([]);
  const [teamDialog, setTeamDialog] = useState({ open: false, registrationId: "", members: [], leaderName: "", leaderEmail: "" });
  const [quizDialog, setQuizDialog] = useState({ open: false, eventItem: null });
  const [selectedRegistrationId, setSelectedRegistrationId] = useState("");
  const [selectedWorkflowStep, setSelectedWorkflowStep] = useState("registration");
  const [eventExpenses, setEventExpenses] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [feedbackRows, setFeedbackRows] = useState([]);
  const [certificateRows, setCertificateRows] = useState([]);
  const [internalStep, setInternalStep] = useState("registration");
  const [registrationSearch, setRegistrationSearch] = useState("");
  const [feedbackSchemaText, setFeedbackSchemaText] = useState('{"questions":[]}');
  const [certUploadFile, setCertUploadFile] = useState(null);
  const [workflowInputs, setWorkflowInputs] = useState({
    confirmationDate: "",
    authorityName: "",
    approvedAmount: "",
    fundAmount: "",
    fundRemarks: "",
    mode: "",
    bookingInfo: "",
    accommodation: "",
    attendedDate: "",
    attendeesCount: "",
    result: "",
    returnDate: "",
    expenseSummary: "",
    expenseNotes: "",
    reimbursementStatus: "",
    reimbursementStudentName: "",
    reimbursementBankAccount: "",
    closeNotes: ""
  });

  useEffect(() => {
    let unsub = null;
    async function init() {
      unsub = ev.listenEvent(eventId, (data) => setEvent(data));
      const regs = await ev.getRegistrations(eventId);
      setRegistrations(regs);
      if (regs.length > 0) setSelectedRegistrationId(regs[0].id);
      const exps = await ev.getEventExpenses(eventId);
      setEventExpenses(exps);
      const att = await ev.getEventAttendance(eventId);
      setAttendanceRows(att);
      const fb = await ev.getEventFeedback(eventId);
      setFeedbackRows(fb);
      const certs = await ev.getEventCertificates(eventId);
      setCertificateRows(certs);
    }
    init();
    return () => { if (unsub) unsub(); };
  }, [eventId]);

  useEffect(() => {
    if (!event) return;
    const schema = event.feedbackSchema || { questions: [] };
    setFeedbackSchemaText(JSON.stringify(schema, null, 2));
  }, [event]);

  const openStage = (st) => {
    setStageDialog({
      open: true,
      stage: st,
      newStatus: st.status,
      dueDate: st.data?.dueDate || "",
      progress: st.data?.progress || 0
    });
    setStageForms((prev) => ({
      ...prev,
      [st.key]: { ...(st.data || {}) }
    }));
  };

  const updateStage = async () => {
    try {
      await ev.setTimelineStageStatus(eventId, stageDialog.stage.key, stageDialog.newStatus, {
        dueDate: stageDialog.dueDate || null,
        progress: Math.max(0, Math.min(100, Number(stageDialog.progress || 0)))
      });
      setStageDialog({ open: false, stage: null, newStatus: "active", dueDate: "", progress: 0 });
    } catch (e) {
      setError(e.message || "Failed to update stage");
    }
  };

  const fieldsFor = (st) => {
    const t = event?.type || "internal";
    const k = st.key;
    if (t === "internal") {
      switch (k) {
        case "registration": return [{ name: "registrationLink", label: "Registration Link", type: "text" }, { name: "live", label: "Registration Live", type: "text" }];
        case "attendance": return [{ name: "attendeesCount", label: "Attendees Count", type: "number" }, { name: "notes", label: "Notes", type: "text" }];
        case "feedback": return [{ name: "feedbackFormUrl", label: "Feedback Form URL", type: "text" }, { name: "responsesCount", label: "Responses Count", type: "number" }];
        case "certificates": return [{ name: "bundleUrl", label: "Certificates Bundle URL", type: "text" }];
        default: return [];
      }
    } else {
      switch (k) {
        case "registration": return [{ name: "registrationLink", label: "Registration Link", type: "text" }];
        case "confirmation": return [{ name: "confirmationDate", label: "Confirmation Date", type: "date" }];
        case "approval": return [{ name: "authorityName", label: "Authority Name", type: "text" }, { name: "approvedAmount", label: "Approved Amount", type: "number" }];
        case "travel": return [{ name: "mode", label: "Mode of Travel", type: "text" }, { name: "bookingInfo", label: "Booking Info", type: "text" }, { name: "accommodation", label: "Accommodation Details", type: "text" }];
        case "attendance": return [{ name: "attendedDate", label: "Attended Date", type: "date" }, { name: "attendeesCount", label: "Attendees Count", type: "number" }];
        case "outcome": return [{ name: "result", label: "Outcome / Result", type: "text" }];
        case "return": return [{ name: "returnDate", label: "Return Date", type: "date" }];
        case "expenses": return [{ name: "expenseSummary", label: "Total Expense", type: "number" }, { name: "notes", label: "Notes", type: "text" }];
        case "reimburse": return [{ name: "reimbursementStatus", label: "Reimbursement Status", type: "text" }];
        case "closure": return [{ name: "closeNotes", label: "Closure Notes", type: "text" }];
        default: return [];
      }
    }
  };

  const setField = (stageKey, name, value) => {
    setStageForms((prev) => ({ ...prev, [stageKey]: { ...(prev[stageKey] || {}), [name]: value } }));
  };

  const saveStageData = async (st) => {
    try {
      await ev.setTimelineStageStatus(eventId, st.key, st.status, stageForms[st.key] || {});
      setSuccess("Stage data saved");
    } catch (e) {
      setError(e.message || "Failed to save data");
    }
  };

  const markAttendance = async (reg) => {
    try {
      await ev.markAttendanceAdmin(eventId, reg.userId, !reg.attended);
      const regs = await ev.getRegistrations(eventId);
      setRegistrations(regs);
    } catch (e) {
      setError(e.message || "Failed to mark attendance");
    }
  };

  const setCertificate = async (reg, url) => {
    try {
      await ev.setUserCertificate(eventId, reg.userId, url);
      const regs = await ev.getRegistrations(eventId);
      setRegistrations(regs);
      setSuccess("Certificate link saved");
    } catch (e) {
      setError(e.message || "Failed to save certificate");
    }
  };

  const updateDialogMember = (idx, field, value) => {
    setTeamDialog((prev) => {
      const copy = (prev.members || []).slice();
      copy[idx] = { ...(copy[idx] || {}), [field]: value };
      return { ...prev, members: copy };
    });
  };

  const addDialogMember = () => {
    setTeamDialog((prev) => ({ ...prev, members: [...(prev.members || []), { name: "", phone: "", email: "" }] }));
  };

  const removeDialogMember = (idx) => {
    setTeamDialog((prev) => {
      const copy = (prev.members || []).slice();
      copy.splice(idx, 1);
      return { ...prev, members: copy };
    });
  };

  const saveDialogTeam = async () => {
    try {
      await ev.adminUpdateRegistrationTeam(teamDialog.registrationId, teamDialog.members || []);
      const regs = await ev.getRegistrations(eventId);
      setRegistrations(regs);
      const exps = await ev.getEventExpenses(eventId);
      setEventExpenses(exps);
      setSuccess("Team updated");
      setTeamDialog({ open: false, registrationId: "", members: [], leaderName: "", leaderEmail: "" });
    } catch (e) {
      setError(e.message || "Failed to update team");
    }
  };

  const selectedRegistration = useMemo(
    () => registrations.find((r) => r.id === selectedRegistrationId) || registrations[0] || null,
    [registrations, selectedRegistrationId]
  );
  const selectedWorkflow = selectedRegistration?.workflow || {};
  const selectedExpenses = (eventExpenses || []).filter((ex) => ex.userId === selectedRegistration?.userId);

  useEffect(() => {
    setWorkflowInputs({
      confirmationDate: selectedWorkflow.confirmationDate ?? "",
      authorityName: selectedWorkflow.authorityName ?? "",
      approvedAmount: selectedWorkflow.approvedAmount ?? "",
      fundAmount: selectedWorkflow.fundAmount ?? "",
      fundRemarks: selectedWorkflow.fundRemarks ?? "",
      mode: selectedWorkflow.mode ?? "",
      bookingInfo: selectedWorkflow.bookingInfo ?? "",
      accommodation: selectedWorkflow.accommodation ?? "",
      attendedDate: selectedWorkflow.attendedDate ?? "",
      attendeesCount: selectedWorkflow.attendeesCount ?? "",
      result: selectedWorkflow.result ?? "",
      returnDate: selectedWorkflow.returnDate ?? "",
      expenseSummary: selectedWorkflow.expenseSummary ?? "",
      expenseNotes: selectedWorkflow.expenseNotes ?? "",
      reimbursementStatus: selectedWorkflow.reimbursementStatus ?? "",
      reimbursementStudentName: selectedWorkflow.reimbursementStudentName ?? selectedRegistration?.leaderName ?? "",
      reimbursementBankAccount: selectedWorkflow.reimbursementBankAccount ?? "",
      closeNotes: selectedWorkflow.closeNotes ?? ""
    });
  }, [
    selectedRegistration?.id,
    selectedWorkflow.confirmationDate,
    selectedWorkflow.authorityName,
    selectedWorkflow.approvedAmount,
    selectedWorkflow.fundAmount,
    selectedWorkflow.fundRemarks,
    selectedWorkflow.mode,
    selectedWorkflow.bookingInfo,
    selectedWorkflow.accommodation,
    selectedWorkflow.attendedDate,
    selectedWorkflow.attendeesCount,
    selectedWorkflow.result,
    selectedWorkflow.returnDate,
    selectedWorkflow.expenseSummary,
    selectedWorkflow.expenseNotes,
    selectedWorkflow.reimbursementStatus,
    selectedWorkflow.reimbursementStudentName,
    selectedWorkflow.reimbursementBankAccount,
    selectedWorkflow.closeNotes,
    selectedRegistration?.leaderName
  ]);

  const workflowSteps = [
    { key: "registration", label: "External Registration", status: selectedWorkflow.registeredExternally ? "completed" : "pending" },
    { key: "confirmation", label: "Organizer Confirmation", status: selectedWorkflow.organizerConfirmed ? "completed" : "pending" },
    { key: "approval", label: "Approval from Authority", status: selectedWorkflow.fundApproved ? "approved" : "pending" },
    { key: "travel", label: "Travel Arrangement", status: selectedWorkflow.travelArranged ? "approved" : (selectedWorkflow.travelRequested ? "active" : "pending") },
    { key: "attendance", label: "Event Attended", status: selectedWorkflow.eventAttended ? "completed" : "pending" },
    { key: "outcome", label: "Outcome / Result", status: selectedWorkflow.outcomeCompleted ? "completed" : "pending" },
    { key: "return", label: "Return to Organization", status: selectedWorkflow.returnCompleted ? "completed" : "pending" },
    { key: "expenses", label: "Expense Submission", status: selectedExpenses.length > 0 ? "completed" : "pending" },
    { key: "reimburse", label: "Reimbursement Process", status: selectedWorkflow.reimbursementCompleted ? "completed" : "pending" },
    { key: "closure", label: "Closure", status: selectedWorkflow.closureCompleted ? "completed" : "pending" }
  ];

  const saveSelectedWorkflow = async (patch) => {
    try {
      if (!selectedRegistration?.id) return;
      await ev.updateRegistrationWorkflowByAdmin(selectedRegistration.id, patch);
      const regs = await ev.getRegistrations(eventId);
      setRegistrations(regs);
      const exps = await ev.getEventExpenses(eventId);
      setEventExpenses(exps);
      setSuccess("Workflow updated");
    } catch (e) {
      setError(e.message || "Failed to update workflow");
    }
  };

  const filteredRegistrations = registrations.filter((r) => {
    const q = registrationSearch.trim().toLowerCase();
    if (!q) return true;
    const text = `${r.name || ""} ${r.email || ""} ${r.userId || ""} ${r.leaderName || ""} ${r.leaderEmail || ""}`.toLowerCase();
    return text.includes(q);
  });

  const today = new Date().toISOString().slice(0, 10);
  const isEventDate = !!event?.date && event.date === today;
  const attendanceByUser = new Map((attendanceRows || []).map((a) => [a.userId, a]));
  const registrationDone = registrations.length > 0;
  const attendanceDone = registrations.length > 0 && registrations.every((r) => attendanceByUser.has(r.userId));
  const feedbackSchema = event?.feedbackSchema || { questions: [] };
  const feedbackDone = (feedbackRows || []).length > 0;
  const certificatesDone = (certificateRows || []).some((c) => !!c.url);
  const internalSteps = [
    { key: "registration", label: "User Registration", done: registrationDone },
    { key: "attendance", label: "Attendance Tracking", done: attendanceDone },
    { key: "feedback", label: "Feedback Collection", done: feedbackDone },
    { key: "certificates", label: "Certificate Generation", done: certificatesDone }
  ];
  const internalProgress = Math.round((internalSteps.filter((s) => s.done).length / internalSteps.length) * 100);

  const setInternalAttendance = async (reg, present) => {
    try {
      if (!isEventDate) {
        setError("Attendance can only be marked on event date");
        return;
      }
      await ev.markAttendanceAdmin(eventId, reg.userId, present, { markedAt: new Date().toISOString() });
      const att = await ev.getEventAttendance(eventId);
      setAttendanceRows(att);
      setSuccess("Attendance updated");
    } catch (e) {
      setError(e.message || "Failed to update attendance");
    }
  };

  const saveFeedbackSchema = async () => {
    try {
      const parsed = JSON.parse(feedbackSchemaText || "{}");
      if (!Array.isArray(parsed.questions)) {
        setError("Feedback schema must include questions array");
        return;
      }
      await ev.updateEvent(userProfile.uid, eventId, { feedbackSchema: parsed });
      setSuccess("Feedback schema saved");
    } catch (e) {
      setError("Invalid feedback JSON");
    }
  };

  const downloadAttendanceCsv = () => {
    const presentRows = filteredRegistrations.filter((r) => !!attendanceByUser.get(r.userId)?.attended);
    if (presentRows.length === 0) {
      setError("No present students to export");
      return;
    }
    const lines = ["userId,name,email,certificateUrl"];
    for (const r of presentRows) {
      lines.push(`${r.userId || ""},${(r.name || r.leaderName || "").replace(/,/g, " ")},${(r.email || r.leaderEmail || "").replace(/,/g, " ")},`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `event_${eventId}_present_students.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadCertificateCsv = async () => {
    try {
      if (!certUploadFile) {
        setError("Select CSV file first");
        return;
      }
      const text = await certUploadFile.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) {
        setError("CSV must include header and rows");
        return;
      }
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const userIdIdx = headers.indexOf("userid");
      const urlIdx = headers.indexOf("certificateurl");
      if (userIdIdx < 0 || urlIdx < 0) {
        setError("CSV must contain userId and certificateUrl columns");
        return;
      }
      const rows = lines.slice(1).map((line) => {
        const cols = line.split(",");
        return { userId: cols[userIdIdx] || "", url: cols[urlIdx] || "" };
      });
      await ev.bulkUpsertCertificates(eventId, rows);
      const certs = await ev.getEventCertificates(eventId);
      setCertificateRows(certs);
      const regs = await ev.getRegistrations(eventId);
      setRegistrations(regs);
      setSuccess("Certificates updated from CSV");
    } catch (e) {
      setError(e.message || "Failed to upload certificate CSV");
    }
  };


  if (userProfile?.role !== "admin") {
    return (
      <AdminLayout title="Events">
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">Unauthorized Access</Typography>
        </Box>
      </AdminLayout>
    );
  }

  if (!event) {
    return (
      <AdminLayout title="Event Detail">
        <Box sx={{ p: 3 }}>
          <Typography>Loading...</Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={event.title || "Event"}>
      <Box sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Card sx={{ borderTop: "6px solid #003366", mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1, flexWrap: "wrap" }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366" }}>{event.title || "Event"}</Typography>
              <Box>
                <Chip label={(event.type || "internal").toUpperCase()} />
                <Chip label={event.date || "TBD"} sx={{ ml: 1 }} />
                <Chip label={event.venue || "Venue TBA"} sx={{ ml: 1 }} />
              </Box>
            </Box>
            {event.type === "external" && (
              <Button variant="outlined" onClick={() => setQuizDialog({ open: true, eventItem: event })}>Edit Quiz</Button>
            )}
          </CardContent>
        </Card>

        {event.type === "internal" && (
          <Card sx={{ borderTop: "6px solid #003366", mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>Internal Workflow Manager</Typography>
              <Typography color="text.secondary" sx={{ mb: 1 }}>Sequential workflow from registration to certificate delivery</Typography>
              <LinearProgress variant="determinate" value={internalProgress} sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {internalSteps.map((st, idx) => (
                      <Paper
                        key={st.key}
                        onClick={() => setInternalStep(st.key)}
                        sx={{ p: 1.25, border: internalStep === st.key ? "2px solid #003366" : "1px solid #e5e7eb", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>{idx + 1}. {st.label}</Typography>
                        <Chip size="small" label={st.done ? "Completed" : "Pending"} color={st.done ? "success" : "default"} />
                      </Paper>
                    ))}
                  </Box>
                  <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={internalSteps.findIndex((s) => s.key === internalStep) <= 0}
                      onClick={() => {
                        const idx = internalSteps.findIndex((s) => s.key === internalStep);
                        if (idx > 0) setInternalStep(internalSteps[idx - 1].key);
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        const idx = internalSteps.findIndex((s) => s.key === internalStep);
                        if (idx < internalSteps.length - 1) {
                          const cur = internalSteps[idx].key;
                          if (cur === "registration" && !registrationDone) return setError("Complete registration step first");
                          if (cur === "attendance" && !attendanceDone) return setError("Complete attendance step first");
                          if (cur === "feedback" && !feedbackDone) return setError("Complete feedback step first");
                          setInternalStep(internalSteps[idx + 1].key);
                        }
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 2, border: "1px solid #e5e7eb", minHeight: 380 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography sx={{ fontWeight: 800, color: "#003366" }}>
                        {(internalSteps.find((s) => s.key === internalStep)?.label) || "Workflow Step"}
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Search student"
                        value={registrationSearch}
                        onChange={(e) => setRegistrationSearch(e.target.value)}
                        sx={{ width: 220 }}
                      />
                    </Box>

                    {internalStep === "registration" && (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Student</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>User ID</TableCell>
                            <TableCell>Registered At</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredRegistrations.map((r) => (
                            <TableRow key={r.id}>
                              <TableCell>{r.name || r.leaderName || "-"}</TableCell>
                              <TableCell>{r.email || r.leaderEmail || "-"}</TableCell>
                              <TableCell>{r.userId}</TableCell>
                              <TableCell>{r.createdAt?.seconds ? new Date(r.createdAt.seconds * 1000).toLocaleString() : "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {internalStep === "attendance" && (
                      <Box>
                        <Typography color={isEventDate ? "success.main" : "warning.main"} sx={{ mb: 1 }}>
                          {isEventDate ? "Attendance marking is open today" : "Attendance can only be modified on event date"}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Student</TableCell>
                              <TableCell>User ID</TableCell>
                              <TableCell>Present</TableCell>
                              <TableCell>Marked At</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredRegistrations.map((r) => {
                              const att = attendanceByUser.get(r.userId);
                              return (
                                <TableRow key={r.id}>
                                  <TableCell>{r.name || r.leaderName || "-"}</TableCell>
                                  <TableCell>{r.userId}</TableCell>
                                  <TableCell>
                                    <Checkbox
                                      checked={!!att?.attended}
                                      disabled={!isEventDate}
                                      onChange={(e) => setInternalAttendance(r, e.target.checked)}
                                    />
                                  </TableCell>
                                  <TableCell>{att?.markedAt ? new Date(att.markedAt).toLocaleString() : "-"}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    )}

                    {internalStep === "feedback" && (
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={8}
                          label="Feedback Schema JSON"
                          value={feedbackSchemaText}
                          onChange={(e) => setFeedbackSchemaText(e.target.value)}
                        />
                        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                          <Button variant="contained" onClick={saveFeedbackSchema}>Save Feedback Schema</Button>
                          <Chip label={`Responses: ${feedbackRows.length}`} color={feedbackRows.length > 0 ? "success" : "default"} />
                          <Chip label={`Questions: ${(feedbackSchema.questions || []).length}`} />
                        </Box>
                      </Box>
                    )}

                    {internalStep === "certificates" && (
                      <Box>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                          <Button variant="outlined" onClick={downloadAttendanceCsv}>Download Present Students CSV</Button>
                          <Button variant="outlined" component="label">
                            Select Certificate CSV
                            <input hidden type="file" accept=".csv" onChange={(e) => setCertUploadFile(e.target.files?.[0] || null)} />
                          </Button>
                          <Button variant="contained" onClick={uploadCertificateCsv}>Upload & Map Certificates</Button>
                        </Box>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>User ID</TableCell>
                              <TableCell>Certificate URL</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {certificateRows.map((c) => (
                              <TableRow key={c.id}>
                                <TableCell>{c.userId}</TableCell>
                                <TableCell>{c.url || "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {event.type === "external" && (
          <Card sx={{ borderTop: "6px solid #003366" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>External Workflow Manager</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {registrations.map((r) => {
                      const active = selectedRegistration?.id === r.id;
                      return (
                        <Paper
                          key={r.id}
                          onClick={() => setSelectedRegistrationId(r.id)}
                          sx={{ p: 1.5, cursor: "pointer", border: active ? "2px solid #003366" : "1px solid #e5e7eb" }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>{r.leaderName || r.leaderEmail || r.userId}</Typography>
                          <Typography variant="body2" color="text.secondary">{r.leaderEmail || "-"}</Typography>
                          <Typography variant="caption" color="text.secondary">Team Size: {Array.isArray(r.team) ? r.team.length : 0}</Typography>
                          <Box sx={{ mt: 1 }}>
                            <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setTeamDialog({ open: true, registrationId: r.id, members: r.team || [], leaderName: r.leaderName || "", leaderEmail: r.leaderEmail || "" }); }}>
                              Edit Team
                            </Button>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  {!selectedRegistration ? (
                    <Typography color="text.secondary">No student registration found.</Typography>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {workflowSteps.map((st) => (
                            <Paper
                              key={st.key}
                              onClick={() => setSelectedWorkflowStep(st.key)}
                              sx={{ p: 1, cursor: "pointer", border: selectedWorkflowStep === st.key ? "2px solid #003366" : "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                            >
                              <Typography sx={{ fontWeight: 600 }}>{st.label}</Typography>
                              <Chip
                                label={st.status === "approved" ? "Approved" : st.status === "completed" ? "Completed" : st.status === "active" ? "Pending Approval" : "Pending"}
                                color={st.status === "approved" ? "info" : st.status === "completed" ? "success" : st.status === "active" ? "warning" : "default"}
                                size="small"
                              />
                            </Paper>
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <Paper sx={{ p: 2, border: "1px solid #e5e7eb", minHeight: 320 }}>
                          <Typography sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>
                            {(workflowSteps.find((s) => s.key === selectedWorkflowStep)?.label) || "Step"}
                          </Typography>
                          {selectedWorkflowStep === "registration" && (
                            <FormControlLabel
                              control={<Checkbox checked={!!selectedWorkflow.registeredExternally} onChange={(e) => saveSelectedWorkflow({ registeredExternally: e.target.checked })} />}
                              label="Student has registered on external portal"
                            />
                          )}
                          {selectedWorkflowStep === "confirmation" && (
                            <Box>
                              <FormControlLabel
                                control={<Checkbox checked={!!selectedWorkflow.organizerConfirmed} onChange={(e) => saveSelectedWorkflow({ organizerConfirmed: e.target.checked })} />}
                                label="Organizer confirmation received"
                              />
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                sx={{ mt: 1 }}
                                label="Confirmation Date"
                                InputLabelProps={{ shrink: true }}
                                value={workflowInputs.confirmationDate}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, confirmationDate: e.target.value }))}
                              />
                              <Button variant="contained" sx={{ mt: 1 }} onClick={() => saveSelectedWorkflow({ confirmationDate: workflowInputs.confirmationDate })}>
                                Save Confirmation
                              </Button>
                            </Box>
                          )}
                          {selectedWorkflowStep === "approval" && (
                            <Box>
                              <FormControlLabel
                                control={<Checkbox checked={!!selectedWorkflow.fundApproved} onChange={(e) => saveSelectedWorkflow({ fundApproved: e.target.checked })} />}
                                label="Mark fund approval completed"
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Authority Name"
                                value={workflowInputs.authorityName}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, authorityName: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Approved Amount"
                                type="number"
                                value={workflowInputs.approvedAmount}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, approvedAmount: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Allocated Fund"
                                type="number"
                                value={workflowInputs.fundAmount}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, fundAmount: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Remarks"
                                value={workflowInputs.fundRemarks}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, fundRemarks: e.target.value }))}
                              />
                              <Button
                                variant="contained"
                                sx={{ mt: 1 }}
                                onClick={() => saveSelectedWorkflow({
                                  authorityName: workflowInputs.authorityName,
                                  approvedAmount: Number(workflowInputs.approvedAmount || 0),
                                  fundAmount: Number(workflowInputs.fundAmount || 0),
                                  fundRemarks: workflowInputs.fundRemarks
                                })}
                              >
                                Save Approval Details
                              </Button>
                            </Box>
                          )}
                          {selectedWorkflowStep === "travel" && (
                            <Box>
                              <FormControlLabel
                                control={<Checkbox checked={!!selectedWorkflow.travelArranged} onChange={(e) => saveSelectedWorkflow({ travelArranged: e.target.checked })} />}
                                label="Travel arrangements completed"
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Mode of Travel"
                                value={workflowInputs.mode}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, mode: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Booking Info"
                                value={workflowInputs.bookingInfo}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, bookingInfo: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Accommodation Details"
                                value={workflowInputs.accommodation}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, accommodation: e.target.value }))}
                              />
                              <Button
                                variant="contained"
                                sx={{ mt: 1 }}
                                onClick={() => saveSelectedWorkflow({
                                  mode: workflowInputs.mode,
                                  bookingInfo: workflowInputs.bookingInfo,
                                  accommodation: workflowInputs.accommodation
                                })}
                              >
                                Save Travel Details
                              </Button>
                            </Box>
                          )}
                          {selectedWorkflowStep === "attendance" && (
                            <Box>
                              <FormControlLabel
                                control={<Checkbox checked={!!selectedWorkflow.eventAttended} onChange={(e) => saveSelectedWorkflow({ eventAttended: e.target.checked })} />}
                                label="Mark event attended"
                              />
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                sx={{ mt: 1 }}
                                label="Attended Date"
                                value={workflowInputs.attendedDate}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, attendedDate: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                sx={{ mt: 1 }}
                                label="Attendees Count"
                                value={workflowInputs.attendeesCount}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, attendeesCount: e.target.value }))}
                              />
                              <Button
                                variant="contained"
                                sx={{ mt: 1 }}
                                onClick={() => saveSelectedWorkflow({
                                  attendedDate: workflowInputs.attendedDate,
                                  attendeesCount: Number(workflowInputs.attendeesCount || 0)
                                })}
                              >
                                Save Attendance
                              </Button>
                            </Box>
                          )}
                          {selectedWorkflowStep === "outcome" && (
                            <Box>
                              <FormControlLabel
                                control={<Checkbox checked={!!selectedWorkflow.outcomeCompleted} onChange={(e) => saveSelectedWorkflow({ outcomeCompleted: e.target.checked })} />}
                                label="Mark outcome completed"
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Outcome / Result"
                                value={workflowInputs.result}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, result: e.target.value }))}
                              />
                              <Button variant="contained" sx={{ mt: 1 }} onClick={() => saveSelectedWorkflow({ result: workflowInputs.result })}>
                                Save Outcome
                              </Button>
                            </Box>
                          )}
                          {selectedWorkflowStep === "return" && (
                            <Box>
                              <FormControlLabel
                                control={<Checkbox checked={!!selectedWorkflow.returnCompleted} onChange={(e) => saveSelectedWorkflow({ returnCompleted: e.target.checked })} />}
                                label="Mark return completed"
                              />
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                sx={{ mt: 1 }}
                                label="Return Date"
                                value={workflowInputs.returnDate}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, returnDate: e.target.value }))}
                              />
                              <Button variant="contained" sx={{ mt: 1 }} onClick={() => saveSelectedWorkflow({ returnDate: workflowInputs.returnDate })}>
                                Save Return
                              </Button>
                            </Box>
                          )}
                          {selectedWorkflowStep === "accommodation" && (
                            <FormControlLabel
                              control={<Checkbox checked={!!selectedWorkflow.accommodationArranged} onChange={(e) => saveSelectedWorkflow({ accommodationArranged: e.target.checked })} />}
                              label="Accommodation arrangements completed"
                            />
                          )}
                          {selectedWorkflowStep === "expenses" && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Total Expense Summary"
                                type="number"
                                value={workflowInputs.expenseSummary}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, expenseSummary: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Expense Notes"
                                value={workflowInputs.expenseNotes}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, expenseNotes: e.target.value }))}
                              />
                              <Button
                                variant="contained"
                                onClick={() => saveSelectedWorkflow({
                                  expenseSummary: Number(workflowInputs.expenseSummary || 0),
                                  expenseNotes: workflowInputs.expenseNotes
                                })}
                              >
                                Save Expense Summary
                              </Button>
                              {selectedExpenses.length === 0 ? (
                                <Typography color="text.secondary">No expenses submitted yet.</Typography>
                              ) : selectedExpenses.map((ex) => (
                                <Paper key={ex.id} sx={{ p: 1, border: "1px solid #e5e7eb" }}>
                                  <Typography>{ex.title || "Expense"} • ₹{ex.amount || 0} • {ex.vendor || "-"}</Typography>
                                </Paper>
                              ))}
                            </Box>
                          )}
                          {selectedWorkflowStep === "reimburse" && (
                            <Box>
                              <TextField
                                fullWidth
                                size="small"
                                label="Student Name"
                                value={workflowInputs.reimbursementStudentName}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, reimbursementStudentName: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Bank Account Details"
                                value={workflowInputs.reimbursementBankAccount}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, reimbursementBankAccount: e.target.value }))}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Reimbursement Status"
                                value={workflowInputs.reimbursementStatus}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, reimbursementStatus: e.target.value }))}
                              />
                              <FormControlLabel
                                sx={{ mt: 1 }}
                                control={<Checkbox checked={!!selectedWorkflow.reimbursementCompleted} onChange={(e) => saveSelectedWorkflow({ reimbursementCompleted: e.target.checked })} />}
                                label="Mark reimbursement completed"
                              />
                              <Button
                                variant="contained"
                                sx={{ mt: 1 }}
                                onClick={() => saveSelectedWorkflow({
                                  reimbursementStudentName: workflowInputs.reimbursementStudentName,
                                  reimbursementBankAccount: workflowInputs.reimbursementBankAccount,
                                  reimbursementStatus: workflowInputs.reimbursementStatus
                                })}
                              >
                                Save Reimbursement Details
                              </Button>
                            </Box>
                          )}
                          {selectedWorkflowStep === "closure" && (
                            <Box>
                              <FormControlLabel
                                control={<Checkbox checked={!!selectedWorkflow.closureCompleted} onChange={(e) => saveSelectedWorkflow({ closureCompleted: e.target.checked })} />}
                                label="Mark closure completed"
                              />
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ mt: 1 }}
                                label="Closure Notes"
                                value={workflowInputs.closeNotes}
                                onChange={(e) => setWorkflowInputs((prev) => ({ ...prev, closeNotes: e.target.value }))}
                              />
                              <Button variant="contained" sx={{ mt: 1 }} onClick={() => saveSelectedWorkflow({ closeNotes: workflowInputs.closeNotes })}>
                                Save Closure
                              </Button>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        <Dialog open={teamDialog.open} onClose={() => setTeamDialog({ open: false, registrationId: "", members: [], leaderName: "", leaderEmail: "" })}>
          <DialogTitle>Team Members</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1 }}>Leader: {teamDialog.leaderName} ({teamDialog.leaderEmail})</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {(teamDialog.members || []).map((m, idx) => (
                <Paper key={idx} sx={{ p: 1, border: "1px solid #e5e7eb" }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={m.name || ""}
                        onChange={(e) => updateDialogMember(idx, "name", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Mobile"
                        value={m.phone || ""}
                        onChange={(e) => updateDialogMember(idx, "phone", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        value={m.email || ""}
                        onChange={(e) => updateDialogMember(idx, "email", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Button onClick={() => removeDialogMember(idx)}>Remove</Button>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              <Button variant="outlined" onClick={addDialogMember}>Add Member</Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTeamDialog({ open: false, registrationId: "", members: [], leaderName: "", leaderEmail: "" })}>Close</Button>
            <Button variant="contained" onClick={saveDialogTeam}>Save</Button>
          </DialogActions>
        </Dialog>

        <AdminEventQuizDialog
          open={quizDialog.open}
          eventItem={quizDialog.eventItem}
          onClose={() => setQuizDialog({ open: false, eventItem: null })}
          onSave={async (quiz) => {
            await ev.setEventQuiz(eventId, quiz);
            setQuizDialog({ open: false, eventItem: null });
            setSuccess("Event quiz saved");
          }}
        />

        <Dialog open={stageDialog.open} onClose={() => setStageDialog({ open: false, stage: null, newStatus: "active" })}>
          <DialogTitle>Update Stage Status</DialogTitle>
          <DialogContent>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={stageDialog.newStatus}
                label="Status"
                onChange={(e) => setStageDialog(prev => ({ ...prev, newStatus: e.target.value }))}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Due Date"
              type="date"
              value={stageDialog.dueDate}
              onChange={(e) => setStageDialog(prev => ({ ...prev, dueDate: e.target.value }))}
              sx={{ mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              label="Progress (%)"
              type="number"
              value={stageDialog.progress}
              onChange={(e) => setStageDialog(prev => ({ ...prev, progress: e.target.value }))}
              sx={{ mt: 2 }}
              inputProps={{ min: 0, max: 100 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStageDialog({ open: false, stage: null, newStatus: "active" })}>Cancel</Button>
            <Button variant="contained" onClick={updateStage}>Update</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}
